/* Free gifts — the model
   ---------------------------------------------------------------------------
   Every free gift used to come from Kite, which made a gift free by discounting
   the gift's own line 100%. That left the gift sitting in the Shopify order at
   £0, and Linnworks — which mirrors the order 1:1 and hands its values to the
   courier — declared it at £0 on every parcel crossing a border. Kite cannot
   structure a promotion any other way, and the Linnworks side cannot be
   changed from here.

   So a gift is added at its FULL price, and the gto-floor-mat-gift app (a
   Shopify discount Function, code in ~/gto-floor-mat-gift) takes exactly that
   price off the order instead:

     Simulator Floor Mat   FREE GIFT   £34.95   ← full value, no discount on it
     ART Cockpit                      £129.95
     Free Simulator Floor Mat         -£34.95   ← the app's order discount

   The shopper pays exactly what they did before and the parcel declares the
   same total; the value is spread across the physical items instead of being
   piled onto one and zeroed on the other.

   THE PROMOTIONS LIST. What qualifies, which gifts, how they are added and
   where each promotion runs all live in one shop metafield,
   gift_promotions.config, edited from the free gifts discount's page in the
   admin. snippets/gift-promotions-data.liquid renders the promotions this
   shopper can have — switched on, and allowed in their country — into
   #gift-promotions, and this file reads that. The discount reads the same
   list, so what is offered here and what is paid for there cannot drift
   apart. Nothing about any one promotion is written into this file.

   Two modes:
     auto    the gift goes into the cart by itself as soon as the cart
             qualifies (the armrest pads, the shaker);
     choose  the shopper picks one of several (the floor mat liveries) —
             gift-promo-chooser.js on the product page, gift-promo-sidecart.js
             in the drawer.

   WHY THIS FILE ADDS THE GIFT ITSELF. The free-gift app this replaced, Kite
   (removed September 2026), zeroed each gift's own line, and Linnworks passed
   that £0 to the courier as its customs value. Here the gift goes in at full
   price and the gto-floor-mat-gift app's discount takes that price off the
   order instead. Some older comments in this theme call Kite "Globo": its
   storefront namespace was window.gfg and its markup gfgFreeGift*.

   FAILS CLOSED, and the failure it guards against is the opposite of Kite's.
   Kite's risk was a gift that did not come back free; ours is a gift the
   discount does not pay for, which would CHARGE someone for something they
   were told was free. Every add is checked against the cart total afterwards
   and rolled back if that total moved — see addGift. And a gift is taken back
   out whenever the cart stops earning it — see reconcile. */

(function () {
  if (window.GiftPromo) return;

  /* Stamped on every gift line, holding the promotion's id. It is what tells a
     gift apart from the same product bought at the same price, and what the
     discount matches on. Deliberately not one of Kite's property names:
     _free_product is what Kite's own discount matches on, so reusing it would
     zero the line and undo the point of all this.

     Leading underscore keeps it off the cart, the checkout and the customer's
     invoice, while still reaching Linnworks for anyone who wants to see which
     items went out as gifts. */
  var GIFT_PROPERTY = "_gift_promo";

  // How far the cart total may legitimately move across an add. The discount
  // and the gift are the same figure, so the honest answer is zero; a penny of
  // slack absorbs rounding without letting a real failure through.
  var TOTAL_TOLERANCE = 2;

  // ── The promotions list ───────────────────────────────────────────────────

  var promotionsCache = null;

  /* Already filtered by gift-promotions-data.liquid to what this shopper can
     have. A missing or malformed list is an empty one: no gift is offered, and
     none can end up in the cart unpaid for. */
  function promotions() {
    if (promotionsCache) return promotionsCache;
    promotionsCache = [];
    var el = document.getElementById("gift-promotions");
    if (!el) return promotionsCache;
    try {
      var list = JSON.parse(el.textContent);
      if (Array.isArray(list)) {
        promotionsCache = list.filter(function (p) {
          return p && p.id && (p.mode === "auto" || p.mode === "choose") &&
            Array.isArray(p.gifts) && p.gifts.length &&
            Array.isArray(p.triggers) && p.triggers.length;
        });
      }
    } catch (e) {
      promotionsCache = [];
    }
    return promotionsCache;
  }

  function promotion(id) {
    return promotions().filter(function (p) { return p.id === id; })[0] || null;
  }

  // The list holds Admin API ids ("gid://shopify/Product/123"); the cart and
  // the storefront speak in bare numbers.
  function numeric(gid) {
    return String(gid == null ? "" : gid).split("/").pop();
  }

  /* A gift's price as the store writes it — "€41,95" on the EU store, where
     the money format uses a comma, so a "Worth" or "value" reads the same as
     every other price on the page. theme.settings carries shop.money_format,
     which is the active currency's; formatMoney is vendor.min.js's. Without
     them, the three currencies the store sells in by symbol, and nothing for
     any other rather than a wrong symbol. */
  function money(minor) {
    var format = window.theme && theme.settings && theme.settings.money_with_currency_format;
    if (format && typeof window.formatMoney === "function") return window.formatMoney(minor, format);
    var code = (window.Shopify && Shopify.currency && Shopify.currency.active) || "GBP";
    var sym = { GBP: "£", EUR: "€", USD: "$" }[code];
    return sym ? sym + (minor / 100).toFixed(2) : "";
  }

  // ── Cart reading ──────────────────────────────────────────────────────────

  // The last cart read, for what has to be decided without waiting for one —
  // a gift riding along with Add to cart (see rideAlong).
  var lastCart = null;

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (cart) {
        lastCart = cart;
        return cart;
      });
  }

  function tagOf(item) {
    var props = item.properties || {};
    return props[GIFT_PROPERTY] || "";
  }

  /* Would this product, in this variant, earn the promotion? Shared by the cart
     check below and the buy-button strip, which asks it of the variant the
     shopper has selected before anything is in the cart. */
  function variantQualifies(promo, productId, variantId) {
    return promo.triggers.some(function (t) {
      if (numeric(t.product) !== String(productId)) return false;
      var variants = Array.isArray(t.variants) ? t.variants : [];
      if (!variants.length) return true;
      return variants.some(function (v) { return numeric(v.id) === String(variantId); });
    });
  }

  /* Lines that earn the promotion. A gift line never earns anything, so no
     promotion can trigger itself or another. */
  function triggerLines(cart, promo) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(function (item) {
      if (tagOf(item)) return false;
      return variantQualifies(promo, item.product_id, item.variant_id);
    });
  }

  function qualifies(cart, promo) {
    return triggerLines(cart, promo).length > 0;
  }

  /* This promotion's gift line, if there is one. Matched on the tag rather than
     the variant, so a shopper who has genuinely bought one of the gift products
     is still offered the gift rather than told they already have it. */
  function giftLine(cart, promo) {
    if (!cart || !cart.items) return null;
    return cart.items.filter(function (item) { return tagOf(item) === promo.id; })[0] || null;
  }

  // What earned it, so copy can name the product rather than say "your order".
  function triggerTitle(cart, promo) {
    var hit = triggerLines(cart, promo)[0];
    return hit ? hit.product_title || hit.title || "" : "";
  }

  // ── Live gift data ────────────────────────────────────────────────────────

  /* Price and stock come from Shopify rather than from the list, which holds
     neither: a price written down is a price in one currency, frozen on the day
     it was typed. These gifts sell at £34.95 and €41.95, and the euro figure is
     not a conversion of the pound one.

     One fetch per gift, cached for the page. A failure leaves price null and
     stock unknown: the gift renders with no value shown — worse copy, not a
     wrong offer — and is still tried, because a failed fetch is usually a
     renamed product (the list holds its handle from the day it was saved), and
     Shopify itself refuses the add if the gift really is sold out. */
  var liveCache = {};

  function fetchLive(gift) {
    var key = numeric(gift.variant);
    if (liveCache[key]) return liveCache[key];

    liveCache[key] = fetch("/products/" + gift.handle + ".js", {
      headers: { Accept: "application/json" },
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (product) {
        var variant = (product.variants || []).filter(function (v) {
          return String(v.id) === key;
        })[0];
        if (!variant) return { price: null, available: false, image: "", title: product.title || "" };
        return {
          price: variant.price,
          available: variant.available !== false,
          image: variant.featured_image
            ? variant.featured_image.src
            : (product.featured_image || (product.images || [])[0] || ""),
          title: product.title || "",
        };
      })
      .catch(function () {
        return { price: null, available: null, image: "", title: "" };
      });

    return liveCache[key];
  }

  // The promotion's gifts that can be had right now, with live data. Known to
  // be sold out is dropped; unknown is kept — see fetchLive.
  function options(promoId) {
    var promo = promotion(promoId);
    if (!promo) return Promise.resolve([]);
    return Promise.all(promo.gifts.map(fetchLive)).then(function (live) {
      return promo.gifts.map(function (gift, i) {
        return {
          variantId: numeric(gift.variant),
          productId: numeric(gift.product),
          handle: gift.handle,
          title: live[i].title || gift.label || "",
          label: gift.label || live[i].title || "",
          price: live[i].price,
          available: live[i].available,
          image: live[i].image,
        };
      }).filter(function (gift) { return gift.available !== false; });
    });
  }

  // ── Writing ───────────────────────────────────────────────────────────────

  function setLineQuantity(key, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).catch(function () {});
  }

  function removeLine(key) {
    return setLineQuantity(key, 0);
  }

  /* Adds a promotion's gift at its own price and confirms the discount paid
     for it.

     The check is the cart total, not the line: the gift is supposed to keep its
     price, and the saving is a separate order discount. The only honest test of
     "did the shopper get this free" is whether what they owe changed.

     So: read the total, add the gift, read the total again. The discount is
     worked out by Shopify at cart level and can lag the write by a moment,
     hence one retry before giving up. If the total really did rise, the
     discount is not applying — it may be off, expired, or limited to other
     customers — and the gift comes straight back out. A shopper charged
     £34.95 under the words "free gift" is the one outcome worth any amount of
     caution.

     window.GiftPromo.busy is published so a second surface does not paint "you
     have a gift waiting" over an add already on its way. */
  var inFlight = {};

  // knownCart: a cart the caller has only just read, sparing another read
  // before the add. Anything else reads its own.
  function addGift(promoId, variantId, knownCart) {
    var promo = promotion(promoId);
    if (!promo) return Promise.reject(new Error("no such promotion"));
    var known = promo.gifts.some(function (g) { return numeric(g.variant) === String(variantId); });
    if (!known) return Promise.reject(new Error("not one of this promotion's gifts"));
    if (inFlight[promoId]) return inFlight[promoId];

    window.GiftPromo.busy = true;
    var settle = function () {
      delete inFlight[promoId];
      window.GiftPromo.busy = Object.keys(inFlight).length > 0;
    };

    inFlight[promoId] = addGiftInner(promo, variantId, knownCart).then(function (result) {
      settle();
      return result;
    }, function (error) {
      settle();
      throw error;
    });
    return inFlight[promoId];
  }

  function addGiftInner(promo, variantId, knownCart) {
    return (knownCart ? Promise.resolve(knownCart) : getCart()).then(function (before) {
      // Never a second copy: another surface may have got there first.
      if (giftLine(before, promo)) return { ok: true, cart: before };

      var properties = {};
      properties[GIFT_PROPERTY] = promo.id;

      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: properties }] }),
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(function (response) {
          var added = (response.items || [response])[0];
          if (!added || !added.key) return Promise.reject(new Error("no line returned"));
          return confirmFree(added.key, before.total_price, 0);
        });
    });
  }

  function confirmFree(key, baseline, attempt) {
    return getCart().then(function (after) {
      // Only a rise means the shopper is paying for the gift. The total can
      // also fall: a product-level code (the 5% affiliate codes) takes its cut
      // of the full-price gift line while the gift discount still covers the
      // gift's whole price, so a free shaker under GTO5% lowers the total by
      // 74p — treating that as a failure removed a gift the shopper was owed.
      if (after.total_price - baseline <= TOTAL_TOLERANCE) {
        announce(after);
        repaint();
        return { ok: true, cart: after };
      }
      if (attempt < 1) {
        return new Promise(function (resolve) {
          setTimeout(function () { resolve(confirmFree(key, baseline, attempt + 1)); }, 700);
        });
      }
      return removeLine(key).then(getCart).then(function (cart) {
        announce(cart);
        repaint();
        return {
          ok: false,
          cart: cart,
          reason: "The gift discount did not apply, so nothing has been added and nothing charged.",
        };
      });
    });
  }

  /* Takes gifts back out when the cart stops earning them.

     Without this a gift is left behind at full price the moment someone
     removes what earned it: the discount no longer applies, so the line
     silently becomes a purchase the shopper never chose to make. Kite could not
     go wrong this way — a 100%-off line stays free whatever else happens to the
     cart — so this is a duty the new structure brings with it.

     Covers two cases: the qualifying product has gone, and the promotion
     itself is no longer one this shopper can have (switched off, or they have
     moved to a country it does not run in). Either way the discount will not
     pay for the gift, so it cannot stay. */
  function strandedGifts(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(function (item) {
      var id = tagOf(item);
      if (!id) return false;
      var promo = promotion(id);
      return !promo || !qualifies(cart, promo);
    });
  }

  /* A promotion gives one of its gift. A second unit on the gift line is not
     covered by the discount, so it would be charged under a "Free gift" badge —
     whether the shopper pressed + on it or a gift riding along with Add to
     cart met one already there (see rideAlong). Back to one. */
  function surplusGifts(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(function (item) { return tagOf(item) && item.quantity > 1; });
  }

  /* Gifts the discount is not paying for. The discount is one order discount
     titled with the promotion's title, or "Free gifts" when several apply (see
     the gto-floor-mat-gift Function), so between them those titles must cover
     every gift in the cart. A gift added through addGift is checked by the
     cart total there; one that rode in with the product it came with never
     had that check, and this is it. */
  function uncoveredGifts(cart) {
    if (!cart || !cart.items) return [];
    var gifts = cart.items.filter(function (item) {
      var promo = promotion(tagOf(item));
      return promo && qualifies(cart, promo);
    });
    if (!gifts.length) return [];

    var titles = promotions().map(function (p) { return p.title; }).concat(["Free gifts"]);
    var covered = (cart.cart_level_discount_applications || []).filter(function (d) {
      return titles.indexOf(d.title) !== -1;
    }).reduce(function (sum, d) { return sum + (d.total_allocated_amount || 0); }, 0);
    var owed = gifts.reduce(function (sum, item) { return sum + item.final_line_price; }, 0);
    return covered + TOTAL_TOLERANCE >= owed ? [] : gifts;
  }

  function reconcile(cart) {
    var stranded = strandedGifts(cart);
    var surplus = surplusGifts(cart).filter(function (line) { return stranded.indexOf(line) === -1; });
    if (!stranded.length && !surplus.length) return reconcileCover(cart);

    return stranded.reduce(function (chain, line) {
      return chain.then(function () {
        forgetAutoAdd(tagOf(line));
        return removeLine(line.key);
      });
    }, surplus.reduce(function (chain, line) {
      return chain.then(function () { return setLineQuantity(line.key, 1); });
    }, Promise.resolve())).then(getCart).then(function (fresh) {
      /* The lines are out of the cart, but every surface showing it is still
         painted from before — which is what a shopper removing their cockpit
         used to see: the mat sitting there at full price in a drawer that had
         not been redrawn. announce() only reaches this file's own listeners;
         the cart itself needs the theme's signal. */
      announce(fresh);
      repaint();
      return reconcileCover(fresh).then(function (result) {
        return { changed: true, cart: result.cart };
      });
    });
  }

  /* An uncovered gift is looked at twice before it goes: Shopify works the
     discount out at cart level and can lag the write that added the gift by a
     moment (the same allowance confirmFree makes). Still uncovered, it comes
     out, and the attempt is remembered so it is not put straight back. */
  function reconcileCover(cart) {
    if (!uncoveredGifts(cart).length) return Promise.resolve({ changed: false, cart: cart });

    return new Promise(function (resolve) { setTimeout(resolve, 700); })
      .then(getCart)
      .then(function (again) {
        var uncovered = uncoveredGifts(again);
        if (!uncovered.length) return { changed: false, cart: again };

        return uncovered.reduce(function (chain, line) {
          return chain.then(function () {
            rememberAutoAdd(tagOf(line), again.token);
            return removeLine(line.key);
          });
        }, Promise.resolve()).then(getCart).then(function (fresh) {
          announce(fresh);
          repaint();
          return { changed: true, cart: fresh };
        });
      });
  }

  /* ── Automatic gifts ──────────────────────────────────────────────────────
     An auto promotion's gift is added as soon as the cart qualifies. If the
     shopper then takes it out, that is their choice and it is not forced back
     in: the add is remembered against this cart, and a gift that was added and
     is now missing while the cart still qualifies is read as declined. When
     the cart stops qualifying the memory is cleared, so adding the qualifying
     product again brings the gift back as expected.

     A failed add is remembered the same way and for the same reason. If the
     discount did not pay for the gift — switched off, expired, or limited to
     other customers — trying again on every cart event just flashes the gift
     on at full price and back off again (seen on the dev theme: added,
     removed, added, removed). One attempt per qualifying spell is enough.

     Remembered in sessionStorage keyed by the cart's token, so it never
     outlives the cart it was about. Storage can be unavailable (private
     windows, blocked site data); then every refresh looks like a first visit
     and a removed gift comes back — annoying, but free. */
  var AUTO_KEY = "gift-promo:auto:";

  function rememberAutoAdd(promoId, token) {
    try { sessionStorage.setItem(AUTO_KEY + promoId, token); } catch (e) {}
  }

  function forgetAutoAdd(promoId) {
    try { sessionStorage.removeItem(AUTO_KEY + promoId); } catch (e) {}
  }

  function declined(promoId, token) {
    try { return sessionStorage.getItem(AUTO_KEY + promoId) === token; } catch (e) { return false; }
  }

  function autoAdd(cart) {
    /* A cart that has stopped qualifying ends the spell, so the next
       qualifying product brings the gift back. Only removing a stranded gift
       used to clear this, which missed a shopper who took the gift out first
       and the product after: nothing was stranded, the memory stayed, and as
       the cart keeps its token even once emptied, no gift came for the rest
       of the session. */
    promotions().forEach(function (promo) {
      if (promo.mode !== "auto") return;
      if (!qualifies(cart, promo)) forgetAutoAdd(promo.id);
      // In the cart already — riding along with Add to cart, most often — is
      // this cart's attempt, so taking it out afterwards reads as declined.
      else if (giftLine(cart, promo)) rememberAutoAdd(promo.id, cart.token);
    });

    var wanted = promotions().filter(function (promo) {
      return promo.mode === "auto" && qualifies(cart, promo) && !giftLine(cart, promo) &&
        !inFlight[promo.id] && !declined(promo.id, cart.token);
    });
    if (!wanted.length) return Promise.resolve(cart);

    // One after another: each add checks the total against the cart before it,
    // which only means something if nothing else moved in between.
    // The cart just read is the baseline for the first add; each add's result
    // is the baseline for the next, so none of them reads the cart first.
    return wanted.reduce(function (chain, promo) {
      return chain.then(function (current) {
        return options(promo.id).then(function (gifts) {
          if (!gifts.length) return current;
          return addGift(promo.id, gifts[0].variantId, current).then(function (result) {
            // Added or refused, this cart has had its attempt — see above.
            if (result) rememberAutoAdd(promo.id, cart.token);
            return (result && result.cart) || current;
          });
        });
      });
    }, Promise.resolve(cart)).then(function () { return cart; }, function () { return cart; });
  }

  /* cart:refresh is the theme's own "the cart moved, redraw" event — app.js,
     cart.js and sidecart-v2.js all listen for it — so one dispatch covers the
     drawer, the cart page and the bubble without this file knowing which of
     them is on screen.

     The echo flag is because this file listens for cart:refresh too. Dispatch
     is synchronous, so setting it around the call stops our own handler
     spending another read of the cart to discover nothing has changed. */
  var echo = false;

  function repaint() {
    echo = true;
    try {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    } finally {
      echo = false;
    }
  }

  // ── Riding along with Add to cart ─────────────────────────────────────────

  /* On a product page the gift can go in with the product itself, in the same
     request, rather than after it: Shopify's /cart/add takes the form's id and
     an items[] list together, and answers for the form's product with the
     drawer already rendered around both lines. The drawer opens with the gift
     in it instead of gaining it a second or two later, once the product has
     landed and every step of autoAdd has run one after another.

     The page decides what rides: gift-promo-cta.js for an automatic promotion
     whose strip is showing, gift-promo-chooser.js for a floor mat picked before
     the cockpit is in the cart. They register it here; the submit listener
     below adds it to the form only if the variant being submitted qualifies and
     the last cart read has no gift for that promotion and has not declined it.
     A stale read at worst sends a gift already there — Shopify merges it into
     that line, and surplusGifts takes it back to one.

     The discount is checked afterwards rather than around the add: a ride-along
     gift the discount does not pay for is taken out by reconcileCover. And if
     Shopify refuses the request — the gift sold out since the page loaded — it
     refuses the product with it, so the form is sent again on its own and the
     product is never held hostage to its gift. */
  var rides = {};
  var ridesOff = false;
  var rodeWith = null;
  var RIDE_ATTR = "data-gift-promo-ride";

  function rideAlong(promoId, variantId, productId) {
    if (variantId && productId) {
      rides[promoId] = { variantId: String(variantId), productId: String(productId) };
    } else {
      delete rides[promoId];
    }
  }

  function clearRideInputs(form) {
    Array.prototype.forEach.call(form.querySelectorAll("[" + RIDE_ATTR + "]"), function (el) {
      el.remove();
    });
  }

  function hiddenInput(form, name, value) {
    var input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    input.setAttribute(RIDE_ATTR, "");
    form.appendChild(input);
  }

  // Capture on document runs before the theme's own submit handler on the
  // form, which is where the form is read into the request.
  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!form || !form.querySelector || !/\/cart\/add/.test(form.getAttribute("action") || "")) return;
    clearRideInputs(form);
    if (ridesOff || !lastCart) return;

    var idInput = form.querySelector('[name="id"]:not([disabled])');
    var variantId = idInput && idInput.value;
    if (!variantId) return;

    var n = 0;
    Object.keys(rides).forEach(function (promoId) {
      var ride = rides[promoId];
      var promo = promotion(promoId);
      if (!promo || inFlight[promoId]) return;
      if (!variantQualifies(promo, ride.productId, variantId)) return;
      if (giftLine(lastCart, promo)) return;
      if (promo.mode === "auto" && declined(promoId, lastCart.token)) return;
      hiddenInput(form, "items[" + n + "][id]", ride.variantId);
      hiddenInput(form, "items[" + n + "][quantity]", "1");
      hiddenInput(form, "items[" + n + "][properties][" + GIFT_PROPERTY + "]", promoId);
      n++;
    });
    if (!n) return;

    rodeWith = form;
    // Read by now: the theme builds its request in this same dispatch, and a
    // native submission takes its data set before any timer can run.
    setTimeout(function () { clearRideInputs(form); }, 0);
  }, true);

  document.addEventListener("cart:item-added", function () { rodeWith = null; });

  document.addEventListener("product:variant-error", function () {
    if (!rodeWith) return;
    var form = rodeWith;
    rodeWith = null;
    ridesOff = true;
    // After the theme's own clean-up of the failed attempt, which runs next.
    setTimeout(function () {
      if (form.requestSubmit) form.requestSubmit();
    }, 0);
  });

  // ── Announcing ────────────────────────────────────────────────────────────

  /* One event, carrying everything a listener needs to paint without reading
     the cart again: for each promotion this shopper can have, whether the cart
     earns it and the gift line if there is one. The chooser, the drawer
     reminder and the buy-button strip all hang off this. */
  function stateOf(cart) {
    return promotions().map(function (promo) {
      return {
        id: promo.id,
        promotion: promo,
        qualifies: qualifies(cart, promo),
        giftLine: giftLine(cart, promo),
        triggerTitle: triggerTitle(cart, promo),
      };
    });
  }

  function announce(cart) {
    document.dispatchEvent(new CustomEvent("gift-promo:change", {
      detail: { cart: cart, promotions: stateOf(cart) },
    }));
  }

  /* Bursts of cart events collapse into one pass — but a change that lands
     while a pass is running gets a pass of its own, or removing a cockpit
     mid-check would leave its gift behind until something else moved. */
  var refreshing = null;
  var again = false;

  function refresh() {
    if (refreshing) {
      again = true;
      return refreshing;
    }
    refreshing = getCart()
      .then(function (cart) {
        return reconcile(cart).then(function (result) {
          return autoAdd(result.cart).then(function () {
            // reconcile and every add announce as they go; say so once more so
            // a listener that just started has state to paint.
            return getCart().then(function (latest) {
              announce(latest);
              return latest;
            });
          });
        });
      })
      .catch(function () {})
      .then(function (cart) {
        refreshing = null;
        if (again) {
          again = false;
          return refresh();
        }
        return cart;
      });
    return refreshing;
  }

  // ── Public ────────────────────────────────────────────────────────────────

  window.GiftPromo = {
    GIFT_PROPERTY: GIFT_PROPERTY,

    // True while an add is in the air — see addGift.
    busy: false,

    promotions: promotions,
    promotion: promotion,
    options: options,
    getCart: getCart,
    qualifies: qualifies,
    variantQualifies: variantQualifies,
    giftLine: giftLine,
    triggerTitle: triggerTitle,
    addGift: addGift,
    refresh: refresh,
    numeric: numeric,
    money: money,
    rideAlong: rideAlong,
  };

  // The cart can change without this file being the one that changed it.
  ["cart:refresh", "cart:item-added", "line-item:change:end", "cart-drawer:open"]
    .forEach(function (name) {
      document.addEventListener(name, function () {
        if (echo) return;
        refresh();
      });
    });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refresh);
  } else {
    refresh();
  }
})();
